import React, { useEffect, useMemo, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { capitalize, isEmpty, isNil } from 'lodash'
import queryString from 'query-string'
import { format, parseISO, isSameDay } from 'date-fns'
import pluralize from 'pluralize'
import { IconInfoCircle } from '@tabler/icons-react'
import { Banner } from '@/components/banners/Banner'
import { Breadcrumbs } from '@/components/forms/Breadcrumbs/Breadcrumbs'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { CopyButton } from '@/components/buttons/CopyButton'
import { fetchMessageThread } from '@/reducers/messages/messages.actions'
import { fetchScorecardsByOrg } from '@/reducers/callSearch/callSearch.actions'
import { fetchPlaylist } from '@/reducers/playlists/playlists.actions'
import { ManualTaskModal } from '@/views/Calls/components/ManualTaskModal'
import { MarkAsReviewed } from '@/views/Playlists/components/MarkAsReviewed'
import { AddToPlaylist } from '@/views/Playlists/components/AddToPlaylist'
import { PlaylistDrawer } from '@/views/Playlists/components/PlaylistDrawer'
import { ScoreForScorecard } from '@/views/Scorecards/ScoreForScorecard'
import { TranscriptHeader } from '@/components/transcript/TranscriptHeader'
import { hasChannelAccess } from '@/utils/helpers'
import { CHANNEL_TYPE, CHANNEL_NAME_PLURAL } from '@/utils/constants'
import {
  shouldExpandMessage,
  calculateToggleState,
  isSmsOrChatMessage,
  parseExternalId,
  isThreadIdVisible,
} from '@/views/Messages/helpers'
import { MessageTranscript } from './MessageTranscript'
import { CallExplorerTabs } from '../Calls/CallExplorerTabs'
import {
  parseCallExplorerParams,
  updateCallExplorerParams,
  removeCallExplorerParams,
} from '../Calls/components/helpers'
import { ManualTaskForm } from '../Calls/components/ManualTaskForm'
import './MessagesPage.scss'

const CREATE_EMAIL_TASK_MODAL = 'messages-email-modal'

const MessagesPage = () => {
  const dispatch = useDispatch()
  const { id } = useParams()

  const location = useLocation()
  const history = useHistory()
  const { highlightedMessage, keywords: urlKeywords } = parseCallExplorerParams(location)
  const { messageThread, loading: messagesLoading } = useSelector((state) => state.messages)
  const { organizationid: organizationId, edit_qa_copilot: editQaCopilot } = useSelector(
    (state) => state.currentUser
  )
  const {
    compliance_tasks_enabled: complianceTasksEnabled,
    coaching_tasks_enabled: coachingTasksEnabled,
    qa_copilot_enabled: copilotEnabled,
    active_channels: activeChannels = [],
  } = useSelector((state) => state.currentOrganization)
  const isBaltoAdmin = organizationId === 1
  const { scorecardConfigs } = useSelector((state) => state.callSearch.data)

  const { search } = useLocation()
  const { channel: channelURL, playlistUuid } = queryString.parse(search)
  const channel =
    channelURL ?? (isEmpty(messageThread) ? CHANNEL_TYPE.EMAIL : messageThread[0].message_type)

  // Check access against the requested message's own type once it loads: the email default
  // above, or a thread left from the previous message, would bounce chat- and SMS-only orgs.
  const routeMessage = messageThread.find((message) => message.id === Number(id))
  const accessChannel = routeMessage?.message_type ?? channelURL

  useEffect(() => {
    if (!isNil(accessChannel) && !hasChannelAccess(activeChannels, accessChannel, isBaltoAdmin)) {
      history.replace('/explorer')
    }
  }, [activeChannels, accessChannel, isBaltoAdmin, history])

  const { playlist } = useSelector((state) => state.playlists)
  const { playlist_calls: playlistItems = [] } = playlist || {}
  const playlistChannelItems = playlistItems.filter((c) => c.record_type === channel)

  const [searchQuery, setSearchQuery] = useState('')
  const [matchCounts, setMatchCounts] = useState({}) // messageId -> count
  const [matchIndexMap, setMatchIndexMap] = useState({}) // messageId -> [3, 4, 5, ...] (global index)
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0)
  const [selectedEmailForTask, setSelectedEmailForTask] = useState()
  const [allExpanded, setAllExpanded] = useState(null)
  const [selectedKeyword, setSelectedKeyword] = useState(null)
  const [currentMatchMessageId, setCurrentMatchMessageId] = useState(null)
  const [defaultExpandedMessageId, setDefaultExpandedMessageId] = useState(null)

  const totalMatches = Object.values(matchCounts).reduce((sum, c) => sum + c, 0)

  let keywordsState = []
  if (Array.isArray(urlKeywords)) {
    keywordsState = urlKeywords
  } else if (urlKeywords) {
    keywordsState = [urlKeywords]
  }
  const handleToggleAll = () => {
    const result = calculateToggleState({
      allExpanded,
      searchQuery,
      totalMatches,
      currentSearchIndex,
      matchIndexMap,
      selectedKeyword,
    })

    setAllExpanded(result.allExpanded)
    setCurrentSearchIndex(result.currentSearchIndex ?? currentSearchIndex)
    setCurrentMatchMessageId(result.currentMatchMessageId ?? null)

    if (result.allExpanded === false) {
      setDefaultExpandedMessageId(null)
    }
  }
  useEffect(() => {
    if (keywordsState.length > 0) {
      setSelectedKeyword(keywordsState[0])
      setSearchQuery(keywordsState[0])
    }
  }, [])

  const handleClearSearch = () => {
    setSearchQuery('')
    setMatchCounts({})
    setMatchIndexMap({})
    setCurrentSearchIndex(0)
    setSelectedKeyword(null)
  }

  const handleSearchTranscript = (event) => {
    const query = event.target.value
    setSearchQuery(query)

    if (!query) handleClearSearch()
  }

  const handleMatchCountUpdate = (emailId, count) => {
    setMatchCounts((prevCounts) => {
      const newCounts = { ...prevCounts, [emailId]: count }
      const sortedIds = messageThread.map((email) => email.id)
      const newMatchIndicesMap = {}
      let globalMatchIndex = 0

      sortedIds.forEach((id) => {
        const matchCount = newCounts[id] || 0
        newMatchIndicesMap[id] = []
        for (let i = 0; i < matchCount; i++) {
          newMatchIndicesMap[id].push(globalMatchIndex++)
        }
      })

      setMatchIndexMap(newMatchIndicesMap)
      return newCounts
    })
  }

  const handleNextSearchMatch = () => {
    if (totalMatches === 0) return
    setCurrentSearchIndex((prev) => (prev + 1 + totalMatches) % totalMatches)
  }

  const handlePreviousSearchMatch = () => {
    if (totalMatches === 0) return
    setCurrentSearchIndex((prev) => (prev - 1 + totalMatches) % totalMatches)
  }

  const handleKeywordClick = (keyword) => {
    setSelectedKeyword(keyword)
    setSearchQuery(keyword)
    setCurrentSearchIndex(0)
  }

  useEffect(() => {
    if (searchQuery && totalMatches > 0 && !selectedKeyword) {
      const foundMessageId = messageThread.find((message) =>
        (matchIndexMap[message.id] || []).includes(currentSearchIndex)
      )?.id
      setCurrentMatchMessageId(foundMessageId || null)
    } else {
      setCurrentMatchMessageId(null)
    }
  }, [currentSearchIndex, matchIndexMap, searchQuery, totalMatches, selectedKeyword, messageThread])

  useEffect(() => {
    dispatch(fetchMessageThread(id))
  }, [id])

  const lastMessageId = useMemo(() => {
    if (!messageThread || messageThread.length === 0) return null
    return messageThread[messageThread.length - 1].id
  }, [messageThread])

  useEffect(() => {
    if (lastMessageId) {
      setDefaultExpandedMessageId(lastMessageId)
    }
  }, [lastMessageId])

  // we will need metadata to switch, and handle hierarchies
  useEffect(() => {
    if (organizationId && playlistUuid) {
      dispatch(fetchPlaylist(playlistUuid, organizationId))
    }
  }, [organizationId, playlistUuid, id])

  // Try to scroll to the highlighted message reliably. Sometimes the
  // element is not yet in the DOM when this effect runs (due to
  // rendering order), so retry a few times with short delays. Clean up
  // any pending timers when dependencies change or component unmounts.
  const _scrollTimeoutRef = useRef(null)

  useEffect(() => {
    // Ensure we always return a cleanup function (avoids inconsistent-return lint).
    // If there's no highlightedMessage, just clear any pending timer and
    // return the cleanup.
    if (!highlightedMessage) {
      if (_scrollTimeoutRef.current) {
        clearTimeout(_scrollTimeoutRef.current)
        _scrollTimeoutRef.current = null
      }
      return () => {
        if (_scrollTimeoutRef.current) {
          clearTimeout(_scrollTimeoutRef.current)
          _scrollTimeoutRef.current = null
        }
      }
    }

    let attempts = 0
    const maxAttempts = 10
    const attemptDelay = 100

    const tryScroll = () => {
      const el = document.getElementById(highlightedMessage)
      if (el) {
        // Found it — scroll and clear any pending timer.
        try {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        } catch (e) {
          // Defensive: ignore if scrollIntoView throws in some environments (should be rare).
          // eslint-disable-next-line no-console
          console.warn('scrollIntoView failed', e)
        }
        if (_scrollTimeoutRef.current) {
          clearTimeout(_scrollTimeoutRef.current)
          _scrollTimeoutRef.current = null
        }
        return
      }

      // Not found yet — schedule another attempt if we haven't exceeded max.
      if (attempts < maxAttempts) {
        attempts += 1
        _scrollTimeoutRef.current = setTimeout(tryScroll, attemptDelay)
      }
    }

    tryScroll()

    return () => {
      if (_scrollTimeoutRef.current) {
        clearTimeout(_scrollTimeoutRef.current)
        _scrollTimeoutRef.current = null
      }
    }
  }, [messageThread, highlightedMessage])

  let breadcrumbs = [
    {
      label: CHANNEL_NAME_PLURAL[channel],
      link: `/explorer?channel=${channel}`,
    },
  ]
  let playlistCall
  if (playlistUuid && !isEmpty(playlist)) {
    if (playlist.type === 'coaching') {
      breadcrumbs = [
        {
          label: 'Coaching',
          link: '/coaching',
          dataTestId: 'breadcrumb-coaching',
        },
      ]
    } else {
      // eslint-disable-next-line eqeqeq
      playlistCall = playlist.playlist_calls.find((item) => item.message_id == id)
      breadcrumbs = [
        {
          label: 'Playlists',
          link: '/playlists',
          dataTestId: 'breadcrumb-playlists',
        },
        {
          label: playlist.name,
          link: `/playlists/${playlistUuid}?channel=${channel}&organization_id=${organizationId}`,
          dataTestId: 'breadcrumb-playlist',
        },
      ]
    }
  }
  const firstMessage = messageThread[0]
  const isSmsOrChatThread = firstMessage && isSmsOrChatMessage(firstMessage.message_type)
  const shouldShowParsedThread = isSmsOrChatThread && isThreadIdVisible(firstMessage.thread_id)
  const conversationId = shouldShowParsedThread ? parseExternalId(firstMessage.thread_id) || id : id
  const currentLink = (
    <div className="flex-align-center small-gap">
      {playlistCall?.alias ? `${playlistCall.alias} (${conversationId})` : conversationId}
      <CopyButton content={conversationId} showTooltip iconOnly />
    </div>
  )
  const latestMessage = useMemo(() => messageThread[messageThread.length - 1], [messageThread])
  const messageOrgId = latestMessage?.organization_id
  const callId = latestMessage?.call_id
  const userId = latestMessage?.user_id
  const type = latestMessage?.message_type

  useEffect(() => {
    if (copilotEnabled && messageOrgId) {
      dispatch(fetchScorecardsByOrg(messageOrgId))
    }
  }, [copilotEnabled, messageOrgId])

  const applicableConfig = useMemo(() => {
    if (!copilotEnabled || isEmpty(scorecardConfigs) || isEmpty(messageThread)) {
      return null
    }

    return scorecardConfigs.find(
      (config) =>
        config.active &&
        !config.deleted &&
        config.call_type === channel &&
        config.organization_id === messageOrgId
    )
  }, [copilotEnabled, scorecardConfigs, messageThread, channel, messageOrgId])

  const thresholdBannerMessage = useMemo(() => {
    if (!applicableConfig) {
      return null
    }

    const messageCount = messageThread.length

    if (
      !isNil(applicableConfig.max_message_count) &&
      messageCount > applicableConfig.max_message_count
    ) {
      const max = applicableConfig.max_message_count
      return `Only the first ${max} ${pluralize('message', max)} in this thread ${max === 1 ? 'was' : 'were'} scored. New messages won't change the score.`
    }

    if (
      !isNil(applicableConfig.min_message_count) &&
      messageCount < applicableConfig.min_message_count
    ) {
      return `Scoring requires at least ${applicableConfig.min_message_count} ${pluralize('message', applicableConfig.min_message_count)}. This thread has ${messageCount} so far.`
    }

    return null
  }, [applicableConfig, messageThread])

  const playlistCallId = useMemo(() => {
    if (!isEmpty(messageThread)) {
      if (!isEmpty(playlistItems)) {
        return messageThread?.find((msg) =>
          playlistItems?.some((call) => call.call_id === msg.call_id)
        )?.call_id
      }
    }
    return null
  }, [messageThread, playlistItems])

  const selectMessageForTask = (task) => {
    setSelectedEmailForTask(task)
    updateCallExplorerParams(location, history, {
      highlightedMessage: task.anchorId,
    })
  }

  const closeCreateTaskModal = () => {
    setSelectedEmailForTask(undefined)
    removeCallExplorerParams(location, history, ['highlightedMessage'])
  }

  const messageContainsSelectedKeyword = (message) => {
    if (!selectedKeyword) return false
    const messageText = (message?.html_body || message?.transcript || '').toLowerCase()
    return messageText.includes(selectedKeyword.toLowerCase())
  }

  useEffect(() => {
    if (!messageThread || messageThread.length === 0) return
    if (!selectedKeyword) return

    const emailsWithKeyword = messageThread.filter(messageContainsSelectedKeyword).length
    if (emailsWithKeyword === messageThread.length) {
      setAllExpanded(true)
    } else {
      setAllExpanded(false)
    }
  }, [selectedKeyword, messageThread])

  return (
    <>
      <header className="page-header call-explorer-page-header" data-testid="messages-page">
        <div className="flex-align-center medium-gap">
          <Breadcrumbs backLinks={breadcrumbs} currentLink={currentLink} />
        </div>
        <div className="flex-align-center medium-gap">
          <AddToPlaylist callIds={[callId]} organizationId={messageOrgId} callType={channel} />
          <MarkAsReviewed callType={channel} />

          {callId && complianceTasksEnabled && (
            <ManualTaskModal
              callId={callId}
              userId={userId}
              organizationId={messageOrgId}
              type="compliance"
              messageId={id}
              callType={type}
            />
          )}
          {callId && coachingTasksEnabled && (
            <ManualTaskModal
              callId={callId}
              userId={userId}
              organizationId={messageOrgId}
              type="coaching"
              messageId={id}
              callType={type}
            />
          )}
          {copilotEnabled && messageOrgId && (
            <ScoreForScorecard
              callId={callId}
              callType={type}
              organizationId={messageOrgId}
              isDisabled={!editQaCopilot}
              disabledTooltip="You have read-only access"
            />
          )}
        </div>
      </header>

      <div className="call-explorer-page">
        {!isEmpty(messageThread) && !messagesLoading.messageThread && (
          <>
            <div className="flex-column flex-item" style={{ width: '50%' }}>
              <div className="transcript-container" data-testid="transcript-container">
                <TranscriptHeader
                  showTranslateFeature={false}
                  headerTitle={messageThread[messageThread.length - 1].subject}
                  channelType={channel}
                  searchQuery={searchQuery}
                  handleSearchTranscript={handleSearchTranscript}
                  totalMatches={totalMatches}
                  currentSearchIndex={currentSearchIndex}
                  handleNextSearchMatch={handleNextSearchMatch}
                  handlePreviousSearchMatch={handlePreviousSearchMatch}
                  handleClearSearch={handleClearSearch}
                  handleToggleAll={messageThread.length > 1 ? handleToggleAll : undefined}
                  allExpanded={messageThread.length > 1 ? allExpanded : undefined}
                  threadLength={messageThread.length}
                  keywords={keywordsState}
                  onKeywordClick={handleKeywordClick}
                  selectedKeyword={selectedKeyword}
                />
                {thresholdBannerMessage && (
                  <Banner
                    informative
                    icon={<IconInfoCircle />}
                    dataTestId="scoring-threshold-banner"
                  >
                    {thresholdBannerMessage}
                  </Banner>
                )}
                <div className="transcript">
                  {messageThread?.map((message, index) => {
                    const isLastMessage = index === messageThread.length - 1
                    const hasKeyword = messageContainsSelectedKeyword(message)
                    const expanded = shouldExpandMessage({
                      allExpanded,
                      searchQuery,
                      currentMatchMessageId,
                      messageId: message.id,
                      selectedKeyword,
                      hasKeyword,
                      isLastMessage,
                      defaultExpandedMessageId,
                    })

                    // Check if we need a date separator for SMS/CHAT messages
                    const isSmsOrChat = isSmsOrChatMessage(message.message_type)
                    let showDateSeparator = false

                    if (isSmsOrChat && index > 0) {
                      const currentMessageDate = parseISO(message.sent_at)
                      const previousMessageDate = parseISO(messageThread[index - 1].sent_at)
                      const previousIsSmsOrChat = isSmsOrChatMessage(
                        messageThread[index - 1].message_type
                      )

                      showDateSeparator =
                        previousIsSmsOrChat && !isSameDay(currentMessageDate, previousMessageDate)
                    } else if (isSmsOrChat && index === 0) {
                      showDateSeparator = true
                    }

                    return (
                      <React.Fragment key={message.id}>
                        {showDateSeparator && (
                          <div className="silence-block mt">
                            {format(parseISO(message.sent_at), 'MMMM d, yyyy')}
                          </div>
                        )}
                        <MessageTranscript
                          message={message}
                          keywords={keywordsState}
                          searchQuery={searchQuery}
                          currentSearchIndex={currentSearchIndex}
                          matchIndices={matchIndexMap[message.id] || []}
                          setSearchMatchesCount={(count) =>
                            handleMatchCountUpdate(message.id, count)
                          }
                          selectMessageForTask={selectMessageForTask}
                          expanded={expanded}
                          selectedKeyword={selectedKeyword}
                        />
                      </React.Fragment>
                    )
                  })}
                </div>
              </div>
            </div>
            <CallExplorerTabs
              key={id}
              callId={callId}
              organizationId={messageOrgId}
              callType={type}
            />
          </>
        )}
        {playlistUuid && playlistChannelItems.length > 0 && !isNil(playlistCallId) && (
          <PlaylistDrawer items={playlistChannelItems} currentItemId={playlistCallId} />
        )}
      </div>
      {selectedEmailForTask && (
        <BasicModal
          data-testid={CREATE_EMAIL_TASK_MODAL}
          title={`Create ${capitalize(selectedEmailForTask.type)} Task`}
          onClose={closeCreateTaskModal}
          size="small"
          show={!isEmpty(selectedEmailForTask)}
        >
          <ManualTaskForm
            callId={selectedEmailForTask.callId}
            userId={selectedEmailForTask.userId}
            organizationId={messageOrgId}
            type={selectedEmailForTask.type}
            callType={selectedEmailForTask.channel}
            messageId={selectedEmailForTask.messageId}
            onClose={closeCreateTaskModal}
          />
        </BasicModal>
      )}
    </>
  )
}

export default MessagesPage
